// Site-wide progressive enhancement for native file inputs.
//
// Every <input type="file"> on the page is wrapped in a branded control: a green
// "Choose file(s)" button plus a drag-and-drop zone, with a removable chip list of the
// selected files. The native input is kept intact (name, id, accept, multiple,
// validation, submission) and only visually hidden, so the real upload still happens
// through it — the custom UI just drives it via the DataTransfer API.

let generatedIdCounter: number = 0;

/**
 * Replaces the native input's file list and notifies listeners.
 * Native file inputs are read-only except through a DataTransfer, so we rebuild one
 * from the desired files and assign it back, then dispatch a synthetic change event.
 */
function syncFiles(input: HTMLInputElement, files: File[]): void {
    const dataTransfer: DataTransfer = new DataTransfer();
    files.forEach((file: File): void => {
        dataTransfer.items.add(file);
    });
    input.files = dataTransfer.files;
    input.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * Returns true when the file satisfies the input's accept attribute.
 * Supports extensions (".pdf"), exact MIME types ("image/png") and wildcards
 * ("image/*"). An empty accept allows everything.
 */
function accepts(input: HTMLInputElement, file: File): boolean {
    const accept: string = input.accept.trim();
    if (accept === "") {
        return true;
    }

    const name: string = file.name.toLowerCase();
    const type: string = file.type.toLowerCase();

    return accept.split(",").some((raw: string): boolean => {
        const token: string = raw.trim().toLowerCase();
        if (token === "") {
            return false;
        }
        if (token === "*" || token === "*/*") {
            return true;
        }
        if (token.startsWith(".")) {
            return name.endsWith(token);
        }
        if (token.endsWith("/*")) {
            return type.startsWith(token.slice(0, -1));
        }
        return type === token;
    });
}

/**
 * Renders the chip list for the currently selected files, wiring each chip's remove
 * button to drop that file from the input's selection.
 */
function renderChips(input: HTMLInputElement, list: HTMLElement): void {
    list.textContent = "";

    const files: File[] = input.files ? Array.from(input.files) : [];
    files.forEach((file: File, index: number): void => {
        const chip: HTMLSpanElement = document.createElement("span");
        chip.className = "file-upload__chip";

        const label: HTMLSpanElement = document.createElement("span");
        label.className = "file-upload__chip-name";
        label.textContent = file.name;

        const remove: HTMLButtonElement = document.createElement("button");
        remove.type = "button";
        remove.className = "file-upload__chip-remove";
        remove.setAttribute("aria-label", `Remove ${file.name}`);
        remove.textContent = "×";
        remove.addEventListener("click", (): void => {
            const remaining: File[] = Array.from(input.files ?? []).filter(
                (_: File, i: number): boolean => {
                    return i !== index;
                },
            );
            syncFiles(input, remaining);
        });

        chip.appendChild(label);
        chip.appendChild(remove);
        list.appendChild(chip);
    });
}

/**
 * Merges dropped files into the input's selection, honoring the accept filter and the
 * multiple attribute (single inputs keep only the last accepted file).
 */
function handleDroppedFiles(input: HTMLInputElement, dropped: FileList): void {
    const accepted: File[] = Array.from(dropped).filter((file: File): boolean => {
        return accepts(input, file);
    });
    if (accepted.length === 0) {
        return;
    }

    if (!input.multiple) {
        syncFiles(input, [accepted[accepted.length - 1]]);
        return;
    }

    const existing: File[] = input.files ? Array.from(input.files) : [];
    syncFiles(input, existing.concat(accepted));
}

/**
 * Builds the branded drag-and-drop control around a single native file input.
 */
function enhanceInput(input: HTMLInputElement): void {
    if (input.dataset.enhanced === "true") {
        return;
    }
    input.dataset.enhanced = "true";

    if (input.id === "") {
        generatedIdCounter += 1;
        input.id = `file-upload-${generatedIdCounter}`;
    }

    const wrapper: HTMLDivElement = document.createElement("div");
    wrapper.className = "file-upload";

    const dropzone: HTMLDivElement = document.createElement("div");
    dropzone.className = "file-upload__dropzone";

    const button: HTMLLabelElement = document.createElement("label");
    button.className = "btn btn--green file-upload__button";
    button.setAttribute("for", input.id);
    button.textContent = input.multiple ? "Choose files" : "Choose file";

    const hint: HTMLSpanElement = document.createElement("span");
    hint.className = "file-upload__hint";
    hint.textContent = "or drag & drop here";

    const list: HTMLDivElement = document.createElement("div");
    list.className = "file-upload__list";

    // Insert the wrapper where the input is, then move the input inside it.
    input.parentNode?.insertBefore(wrapper, input);
    input.classList.add("file-upload__input");

    dropzone.appendChild(button);
    dropzone.appendChild(hint);
    wrapper.appendChild(dropzone);
    wrapper.appendChild(input);
    wrapper.appendChild(list);

    input.addEventListener("change", (): void => {
        renderChips(input, list);
    });

    dropzone.addEventListener("dragenter", (event: DragEvent): void => {
        event.preventDefault();
        dropzone.classList.add("is-dragover");
    });
    dropzone.addEventListener("dragover", (event: DragEvent): void => {
        event.preventDefault();
        dropzone.classList.add("is-dragover");
    });
    dropzone.addEventListener("dragleave", (): void => {
        dropzone.classList.remove("is-dragover");
    });
    dropzone.addEventListener("drop", (event: DragEvent): void => {
        event.preventDefault();
        dropzone.classList.remove("is-dragover");
        if (event.dataTransfer) {
            handleDroppedFiles(input, event.dataTransfer.files);
        }
    });

    // Reflect any pre-existing selection (e.g. after a server-side validation redisplay).
    renderChips(input, list);
}

/**
 * Enhances every not-yet-enhanced file input on the page.
 */
function enhanceFileInputs(): void {
    const inputs: NodeListOf<HTMLInputElement> =
        document.querySelectorAll<HTMLInputElement>('input[type="file"]');
    inputs.forEach((input: HTMLInputElement): void => {
        enhanceInput(input);
    });
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", enhanceFileInputs);
} else {
    enhanceFileInputs();
}
